import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_BASE_URL, fetchJson } from './api';
import ProjectRepositoryBreadcrumb from './ProjectRepositoryBreadcrumb';
import ProjectRepositoryGrid, { getRepositoryDocumentMeta } from './ProjectRepositoryGrid';
import ProjectRepositoryPreviewDialog from './ProjectRepositoryPreviewDialog';

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'jpg', 'jpeg', 'png', 'webp'];
const CONTENT_TYPES_BY_EXTENSION = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    txt: 'text/plain',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp'
};

const REPOSITORY_COLLECTIONS = [
    { path: '', name: 'All project documents' },
    { path: 'uploads', name: 'Project uploads' },
    { path: 'questionnaire', name: 'Questionnaire evidence' },
    { path: 'issued', name: 'Issued reports' }
];

export function validateRepositoryFile(file) {
    if (!file) return 'Choose a file to upload.';
    if (!Number.isFinite(Number(file.size)) || Number(file.size) <= 0) return 'The selected file is empty.';
    if (Number(file.size) > MAX_FILE_SIZE_BYTES) return 'Files must be 100 MB or smaller.';
    const extension = String(file.name || '').split('.').pop().toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) return 'This file type is not supported.';
    return '';
}

export function getRepositoryContentType(file) {
    const extension = String(file?.name || '').split('.').pop().toLowerCase();
    return String(file?.type || '').trim().toLowerCase() || CONTENT_TYPES_BY_EXTENSION[extension] || 'application/octet-stream';
}

function getAuthHeaders() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function putFile(upload, file, onProgress) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open(upload.method || 'PUT', upload.url, true);
        Object.entries(upload.headers || {}).forEach(([name, value]) => request.setRequestHeader(name, value));
        request.upload.onprogress = (event) => {
            if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
        };
        request.onerror = () => reject(new Error('The upload could not reach document storage.'));
        request.onload = () => {
            if (request.status >= 200 && request.status < 300) resolve();
            else reject(new Error(`Document storage rejected the upload (HTTP ${request.status}).`));
        };
        request.send(file);
    });
}

export default function ProjectRepositoryExplorer({ reference, embedded = false }) {
    const inputRef = useRef(null);
    const [path, setPath] = useState('');
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [sort, setSort] = useState('name');
    const [repository, setRepository] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyItemId, setBusyItemId] = useState('');
    const [uploadState, setUploadState] = useState(null);
    const [preview, setPreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    const visibleFiles = useMemo(() => {
        const source = path
            ? (repository?.files || [])
            : (repository?.all_files || repository?.files || []);
        const term = deferredSearch.trim().toLowerCase();
        const filtered = term
            ? source.filter((file) => String(file?.name || '').toLowerCase().includes(term))
            : [...source];
        filtered.sort((left, right) => {
            if (sort === 'date') return new Date(right?.date || 0).getTime() - new Date(left?.date || 0).getTime();
            if (sort === 'size') return Number(right?.size_bytes || 0) - Number(left?.size_bytes || 0);
            return String(left?.name || '').localeCompare(String(right?.name || ''));
        });
        return filtered;
    }, [deferredSearch, path, repository, sort]);

    const visibleFolders = path ? (repository?.folders || []) : [];
    const selectedMeta = selectedFile ? getRepositoryDocumentMeta(selectedFile) : null;
    const linkedCount = visibleFiles.filter((file) => String(file?.source || '').toLowerCase() === 'questionnaire').length;
    const activeCollectionPath = path ? path.split('/')[0] : '';
    const currentCollectionName = path
        ? (repository?.breadcrumbs?.[repository.breadcrumbs.length - 1]?.name || 'Project documents')
        : 'All project documents';
    const canUploadHere = !path || path === 'uploads';

    const loadRepository = useCallback(async () => {
        if (!reference) return;
        setLoading(true);
        setError('');
        try {
            const query = new URLSearchParams({ mode: 'surveyor', path, sort });
            if (deferredSearch.trim()) query.set('search', deferredSearch.trim());
            const result = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/repository?${query.toString()}`);
            setRepository(result);
        } catch (requestError) {
            setError(requestError?.message || 'Project documents could not be loaded.');
        } finally {
            setLoading(false);
        }
    }, [reference, path, sort, deferredSearch]);

    useEffect(() => { void loadRepository(); }, [loadRepository]);
    useEffect(() => {
        setSelectedFile((current) => visibleFiles.find((file) => file.item_id === current?.item_id) || visibleFiles[0] || null);
    }, [visibleFiles]);
    useEffect(() => () => {
        if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl);
    }, [preview]);

    async function requestAccess(file, disposition) {
        return fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/repository/items/${encodeURIComponent(file.item_id)}/access?mode=surveyor`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ disposition })
        });
    }

    async function materializeProtectedAccess(access) {
        if (!access?.requires_authenticated_fetch) return { url: access?.url || '', objectUrl: '' };
        const response = await fetch(`${API_BASE_URL}${access.url}`, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error('The document could not be opened.');
        const objectUrl = URL.createObjectURL(await response.blob());
        return { url: objectUrl, objectUrl };
    }

    async function handlePreview(file) {
        setBusyItemId(file.item_id);
        setError('');
        try {
            const result = await requestAccess(file, 'inline');
            const resolved = await materializeProtectedAccess(result.access);
            setPreview({ file, name: file.name, contentType: file.content_type, ...resolved });
        } catch (requestError) {
            setError(requestError?.message || 'The document could not be previewed.');
        } finally {
            setBusyItemId('');
        }
    }

    async function handleDownload(file) {
        if (!file) return;
        setBusyItemId(file.item_id);
        setError('');
        try {
            const result = await requestAccess(file, 'attachment');
            const resolved = await materializeProtectedAccess(result.access);
            const anchor = document.createElement('a');
            anchor.href = resolved.url;
            anchor.download = file.name;
            anchor.rel = 'noopener';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            if (resolved.objectUrl) window.setTimeout(() => URL.revokeObjectURL(resolved.objectUrl), 1000);
        } catch (requestError) {
            setError(requestError?.message || 'The document could not be downloaded.');
        } finally {
            setBusyItemId('');
        }
    }

    async function handleUpload(file) {
        const validationError = validateRepositoryFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        setUploadState({ name: file.name, progress: 0, status: 'Preparing upload' });
        try {
            const contentType = getRepositoryContentType(file);
            const intent = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/repository/uploads/intents?mode=surveyor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ original_file_name: file.name, content_type: contentType, size_bytes: file.size })
            });
            setUploadState({ name: file.name, progress: 0, status: 'Uploading' });
            await putFile(intent.upload, file, (progress) => setUploadState({ name: file.name, progress, status: 'Uploading' }));
            setUploadState({ name: file.name, progress: 100, status: 'Verifying' });
            await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/repository/uploads/${intent.file.id}/complete?mode=surveyor`, { method: 'POST' });
            setUploadState({ name: file.name, progress: 100, status: 'Uploaded' });
            await loadRepository();
        } catch (uploadError) {
            setUploadState({ name: file.name, progress: 0, status: 'Failed' });
            setError(uploadError?.message || 'The file could not be uploaded.');
        } finally {
            if (inputRef.current) inputRef.current.value = '';
        }
    }

    function handleDrop(event) {
        event.preventDefault();
        const file = event.dataTransfer?.files?.[0];
        if (file) void handleUpload(file);
    }

    function closePreview() {
        if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl);
        setPreview(null);
    }

    return (
        <Box sx={{ display: 'grid', gap: embedded ? 1.25 : 2 }}>
            <Paper
                variant="outlined"
                onDragOver={(event) => { if (canUploadHere) event.preventDefault(); }}
                onDrop={(event) => { if (canUploadHere) handleDrop(event); }}
                sx={{ borderRadius: 1, overflow: 'hidden', boxShadow: 'none' }}
            >
                <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                        <Box>
                            <Typography component="h2" sx={{ fontSize: 15, fontWeight: 900 }}>Project documents</Typography>
                            <Typography variant="caption" color="text.secondary">Approved project files, questionnaire evidence, and issued reports</Typography>
                        </Box>
                        {canUploadHere ? (
                            <Button
                                variant="outlined"
                                startIcon={<CloudUploadIcon />}
                                aria-label="Upload project file"
                                onClick={() => inputRef.current?.click()}
                                disabled={uploadState?.status === 'Uploading' || uploadState?.status === 'Verifying'}
                            >
                                Upload
                            </Button>
                        ) : null}
                        <input
                            ref={inputRef}
                            hidden
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp"
                            onChange={(event) => { const file = event.target.files?.[0]; if (file) void handleUpload(file); }}
                        />
                    </Stack>
                    {uploadState ? (
                        <Box sx={{ mt: 1 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="caption" sx={{ fontWeight: 750 }}>{uploadState.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{uploadState.status}{uploadState.status === 'Uploading' ? ` ${uploadState.progress}%` : ''}</Typography>
                            </Stack>
                            {['Uploading', 'Verifying'].includes(uploadState.status) ? <LinearProgress variant={uploadState.status === 'Uploading' ? 'determinate' : 'indeterminate'} value={uploadState.progress} sx={{ mt: 0.5 }} /> : null}
                        </Box>
                    ) : null}
                </Box>

                {error ? <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 0 }}>{error}</Alert> : null}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr)' }, minHeight: embedded ? 410 : 540 }}>
                    <Box component="nav" aria-label="Document tree" sx={{ p: 1, bgcolor: 'action.hover', borderRight: { md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider' }}>
                        <Typography sx={{ px: 1, py: 0.75, fontSize: 10, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>Document tree</Typography>
                        <Box sx={{ display: { xs: 'grid', md: 'block' }, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))' }, gap: 0.5 }}>
                            {REPOSITORY_COLLECTIONS.map((collection) => {
                                const active = collection.path === activeCollectionPath;
                                return (
                                    <Button
                                        key={collection.path || 'all'}
                                        fullWidth
                                        size="small"
                                        variant="text"
                                        startIcon={<FolderCopyIcon fontSize="small" />}
                                        onClick={() => { setSearch(''); setPath(collection.path); }}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            px: 1,
                                            py: 0.85,
                                            color: active ? 'text.primary' : 'text.secondary',
                                            bgcolor: active ? 'background.paper' : 'transparent',
                                            fontWeight: active ? 850 : 650,
                                            textTransform: 'none',
                                            border: active ? '1px solid' : '1px solid transparent',
                                            borderColor: active ? 'divider' : 'transparent'
                                        }}
                                    >
                                        {collection.name}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ px: { xs: 1.25, sm: 2 }, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ lg: 'center' }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{currentCollectionName}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {visibleFiles.length} document{visibleFiles.length === 1 ? '' : 's'}{linkedCount ? ` · ${linkedCount} linked to information` : ''}
                                    </Typography>
                                    {path ? <ProjectRepositoryBreadcrumb items={repository?.breadcrumbs || []} onNavigate={(nextPath) => { setSearch(''); setPath(nextPath); }} /> : null}
                                </Box>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                                    <TextField fullWidth size="small" value={search} onChange={(event) => setSearch(event.target.value)} label="Search documents" sx={{ minWidth: { lg: 210 } }} />
                                    <FormControl fullWidth size="small" sx={{ minWidth: { lg: 112 } }}>
                                        <InputLabel id="repository-sort-label">Sort</InputLabel>
                                        <Select labelId="repository-sort-label" value={sort} label="Sort" onChange={(event) => setSort(event.target.value)}>
                                            <MenuItem value="name">Name</MenuItem>
                                            <MenuItem value="date">Newest</MenuItem>
                                            <MenuItem value="size">Largest</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Button variant="outlined" aria-label="Refresh documents" onClick={() => void loadRepository()} sx={{ minWidth: 44, px: 1 }}>
                                        <RefreshIcon />
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>

                        <Box sx={{ minHeight: embedded ? 250 : 360 }}>
                            {loading ? (
                                <Box sx={{ minHeight: embedded ? 250 : 360, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Loading project documents" /></Box>
                            ) : (
                                <ProjectRepositoryGrid
                                    folders={visibleFolders}
                                    files={visibleFiles}
                                    busyItemId={busyItemId}
                                    selectedItemId={selectedFile?.item_id || ''}
                                    onOpenFolder={(nextPath) => { setSearch(''); setPath(nextPath); }}
                                    onSelect={setSelectedFile}
                                    onPreview={handlePreview}
                                    onDownload={handleDownload}
                                />
                            )}
                        </Box>

                        {selectedFile && selectedMeta ? (
                            <Box sx={{ m: { xs: 1.25, sm: 2 }, mt: 1, p: 1.5, bgcolor: 'action.hover', borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 900, color: 'primary.main' }}>Selected document relationship</Typography>
                                <Typography variant="body2" sx={{ mt: 0.65 }}>{selectedMeta.detail}</Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Paper>

            <ProjectRepositoryPreviewDialog preview={preview} onClose={closePreview} onDownload={handleDownload} />
        </Box>
    );
}